import React, { type ReactNode } from 'react';
import { ClearButton, Input, LeadingSlot, Wrapper } from './SearchInput.styled';

export interface SearchInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'children'
  > {
  /** Controlled value. */
  value: string;
  /** Called with the new string whenever the user types. */
  onChange: (value: string) => void;
  /**
   * Custom leading icon (defaults to a Material symbol `search` glyph). Pass
   * any react node — `null` removes the icon entirely.
   */
  leadingIcon?: ReactNode;
  /**
   * When true, a clear button appears once the input has content. Clicking
   * it dispatches `onChange('')`. Defaults to `true`.
   */
  showClear?: boolean;
  /** When true the wrapper grows to fill its container instead of capping at 220px. */
  stretch?: boolean;
  /** Override the clear button aria-label (for i18n). */
  clearLabel?: string;
  dataTestId?: string;
}

const DefaultSearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="m21 21-4.3-4.3M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DefaultClearIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 6l12 12M6 18 18 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    {
      value,
      onChange,
      leadingIcon,
      showClear = true,
      stretch = false,
      clearLabel = 'Clear search',
      placeholder = 'Search',
      dataTestId = 'search-input',
      ...rest
    },
    ref,
  ) {
    const hasValue = value.length > 0;
    const resolvedIcon = leadingIcon === undefined ? <DefaultSearchIcon /> : leadingIcon;

    return (
      <Wrapper $stretch={stretch} data-testid={dataTestId}>
        {resolvedIcon !== null && (
          <LeadingSlot data-testid={`${dataTestId}__icon`}>{resolvedIcon}</LeadingSlot>
        )}
        <Input
          ref={ref}
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={`${dataTestId}__input`}
          {...rest}
        />
        {showClear && hasValue && (
          <ClearButton
            type="button"
            aria-label={clearLabel}
            onClick={() => onChange('')}
            data-testid={`${dataTestId}__clear`}
          >
            <DefaultClearIcon />
          </ClearButton>
        )}
      </Wrapper>
    );
  },
);

export default SearchInput;
